/**
 * Resumen de los huecos de grabación para la leyenda de la barra de histórico
 * y evidencia (#184). La barra pintaba cada hueco con un mínimo de 0,4 % del
 * rango: 55 cortes de ~44 s en 24 h ocupaban el 22 % de la barra y el dueño
 * los veía como «micro huecos por todas partes». Ahora los huecos van a escala
 * real (con mínimo de 1 px en CSS para que no desaparezcan) y este resumen da
 * la magnitud verdadera: cuántos cortes y cuánto tiempo sin grabación.
 */

export interface GapInterval {
  from: string;
  to: string;
}

export interface GapsSummary {
  count: number;
  totalMs: number;
}

export function summarizeGaps(gaps: readonly GapInterval[]): GapsSummary {
  let totalMs = 0;
  for (const gap of gaps) {
    const ms = new Date(gap.to).getTime() - new Date(gap.from).getTime();
    if (Number.isFinite(ms) && ms > 0) totalMs += ms;
  }
  return { count: gaps.length, totalMs };
}

/** Duración humana compacta: «45 s», «3 min», «1 h 12 min». */
export function formatGapDuration(totalMs: number): string {
  const totalSeconds = Math.round(totalMs / 1000);
  if (totalSeconds < 60) return `${totalSeconds} s`;
  const totalMinutes = Math.round(totalSeconds / 60);
  if (totalMinutes < 60) return `${totalMinutes} min`;
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return minutes === 0 ? `${hours} h` : `${hours} h ${minutes} min`;
}

/** Texto de la leyenda: «3 cortes · 2 min sin grabación», o '' si no hay huecos. */
export function describeGaps(gaps: readonly GapInterval[]): string {
  const { count, totalMs } = summarizeGaps(gaps);
  if (count === 0) return '';
  const cortes = count === 1 ? '1 corte' : `${count} cortes`;
  return `${cortes} · ${formatGapDuration(totalMs)} sin grabación`;
}
